import {
  call,
  takeEvery,
  put,
  all,
  select,
  take,
} from "typed-redux-saga/macro";
import {
  GET_REPORT_ANALYTICS_CONFIG_DETAILS,
  LoadReportAnalyticsConfigAction,
  FETCH_PERFORMANCE_METRICS,
  FetchPerformanceMetricsAction,
  FETCH_KPIS_PIE_CHART,
  FetchPieChartDataAction,
  FETCH_KPI_PRODUCTS,
  FetchKPIProductsAction,
  FETCH_ORDERS_BY_LOCATIONS,
  FetchLocationTreeDataAction,
  FETCH_KPI_ANALYSIS,
  FetchKPIAnalysisAction,
  LOAD_ANALYZER_SERVICE_URL,
  FetchDateYearMetricsAction,
  FETCH_DATE_YEAR_METRICS,
  FETCH_PRODUCT_IDS_BY_ANALYTIC_IDS,
  LOAD_KPI_STORE_LIST,
  LoadKPIStoreListAction,
  FETCH_KPI_STORE_LIST,
} from "./PerformanceReportTypes";
import { LOAD_ANALYTICS_SERVICE_URL } from "store/product/ProductTypes";
import { acquireEndpoint } from "utils/SmartMerchandiserAPI";
import { callApi, delay } from "utils/SagaUtils";
import { transformMetricsForPayload } from "utils/PerformanceReportUtils";
import { getData, postData } from "services/ApiService";
import {
  selectPerformanceReportLocale,
  selectPerformanceReportCatalogId,
} from "./PerformanceReportSelectors";
import {
  FETCH_PRODUCT_LIST,
  FETCH_PRODUCTS_COLORS,
} from "store/product-list/ProductListTypes";
import {
  setKPIProductsLoadingState,
  updateKPIProductsDetails,
} from "./PerformanceReportActions";
import {
  isRecordingActive,
  addRecording,
  createRecording,
} from "utils/AnalyticsRecordingUtils";
import {
  selectAccountEntitlements,
  selectCallerAccountId,
} from "store/app-config/AppConfigSelectors";
import AppEnv from "../../utils/AppEnv";

function* getReportAnalyticsConfig(action: LoadReportAnalyticsConfigAction) {
  try {
    const actionType = GET_REPORT_ANALYTICS_CONFIG_DETAILS;
    const constName = Object.keys({
      GET_REPORT_ANALYTICS_CONFIG_DETAILS,
    })[0].toString();
    const serviceType = LOAD_ANALYTICS_SERVICE_URL;

    const headersObj = {
      "x-store-id": action.payload.storeId,
    };
    const optionsObj = {
      showGenericError: false,
    };

    const endpoint = acquireEndpoint(constName, serviceType);
    yield* call(
      callApi,
      actionType,
      getData,
      null,
      endpoint,
      headersObj,
      optionsObj,
    );
  } catch (e: any) {
    console.error(e);
    yield* put({
      type: GET_REPORT_ANALYTICS_CONFIG_DETAILS.FAILURE,
      message: e.message,
    });
  }
}

function* fetchPerformanceMetrics(action: FetchPerformanceMetricsAction) {
  try {
    const { storeId, category, dateRange, comparisonDateRange, metrics } =
      action.payload;

    const serviceType = LOAD_ANALYTICS_SERVICE_URL;
    const constName = Object.keys({
      FETCH_PERFORMANCE_METRICS,
    })[0].toString();
    const endpoint = acquireEndpoint(constName, serviceType);
    const headersObj = {
      "x-store-id": storeId,
    };

    const currentPayload = {
      dateRange,
      metrics: transformMetricsForPayload(metrics),
    };

    const comparisonPayload = {
      dateRange: comparisonDateRange,
      metrics: transformMetricsForPayload(metrics),
    };

    const [currentResponse, comparisonResponse] = yield* all([
      call(
        callApi,
        FETCH_PERFORMANCE_METRICS,
        postData,
        currentPayload,
        endpoint,
        headersObj,
      ),
      call(
        callApi,
        FETCH_PERFORMANCE_METRICS,
        postData,
        comparisonPayload,
        endpoint,
        headersObj,
      ),
    ]);

    if (isRecordingActive()) {
      if (currentResponse?.type === FETCH_PERFORMANCE_METRICS.SUCCESS) {
        const currentRecording = createRecording(
          { dateRange, metrics: transformMetricsForPayload(metrics) },
          currentResponse.payload,
        );
        addRecording(currentRecording);
      }
      if (comparisonResponse?.type === FETCH_PERFORMANCE_METRICS.SUCCESS) {
        const comparisonRecording = createRecording(
          {
            dateRange: comparisonDateRange,
            metrics: transformMetricsForPayload(metrics),
          },
          comparisonResponse.payload,
        );
        addRecording(comparisonRecording);
      }
    }

    yield* put({
      type: FETCH_PERFORMANCE_METRICS.SUCCESS,
      payload: {
        category,
        currentData: currentResponse?.payload,
        comparisonData: comparisonResponse?.payload,
        metrics,
      },
    });
  } catch (e: any) {
    console.error(e);
    yield* put({
      type: FETCH_PERFORMANCE_METRICS.FAILURE,
      payload: {
        category: action.payload.category,
      },
    });
  }
}

export function* watchGetReportAnalyticsConfig() {
  yield* takeEvery(
    GET_REPORT_ANALYTICS_CONFIG_DETAILS.REQUEST,
    getReportAnalyticsConfig,
  );
}

function* fetchPieChartData(action: FetchPieChartDataAction) {
  try {
    const {
      storeId,
      dateRange,
      dimension,
      metrics,
      sortBy,
      limit,
      chartType,
      metricId,
    } = action.payload;

    const serviceType = LOAD_ANALYTICS_SERVICE_URL;
    const constName = Object.keys({
      FETCH_KPIS_PIE_CHART,
    })[0].toString();
    const endpoint = acquireEndpoint(constName, serviceType);
    const headersObj = {
      "x-store-id": storeId,
    };

    const payload = {
      dateRange,
      dimension,
      metrics,
      sortBy,
      limit,
      chartType,
      metricId,
    };

    const response = yield* call(
      callApi,
      FETCH_KPIS_PIE_CHART,
      postData,
      payload,
      endpoint,
      headersObj,
      { meta: { metricId, chartType } },
    );

    if (
      isRecordingActive() &&
      response?.type === FETCH_KPIS_PIE_CHART.SUCCESS
    ) {
      const recording = createRecording(
        { dateRange, dimension, metrics, sortBy, limit },
        response.payload,
      );
      addRecording(recording);
    }
  } catch (e: any) {
    console.error("Pie chart fetch error:", e);
  }
}

function* fetchKPIProducts(action: FetchKPIProductsAction) {
  try {
    const {
      storeId,
      dateRange,
      dimension,
      metrics,
      sortBy,
      limit,
      isProductIdAnalyticIdAPIAvailable,
    } = action.payload;
    const serviceType = LOAD_ANALYTICS_SERVICE_URL;
    const accountId = yield* select(selectCallerAccountId);
    const entitlements = yield* select(selectAccountEntitlements, accountId);
    const hasCatalogEntitlement = entitlements?.includes("catalog");
    const constName = Object.keys({
      FETCH_KPI_PRODUCTS,
    })[0].toString();
    const endpoint = acquireEndpoint(constName, serviceType);
    const headersObj = {
      "x-store-id": storeId,
    };

    const payload = {
      dateRange,
      dimension,
      metrics,
      sortBy,
      limit,
    };

    const response = yield* call(
      callApi,
      FETCH_KPI_PRODUCTS,
      postData,
      payload,
      endpoint,
      headersObj,
    );

    if (isRecordingActive() && response?.type === FETCH_KPI_PRODUCTS.SUCCESS) {
      const recording = createRecording(
        { dateRange, dimension, metrics, sortBy, limit },
        response.payload,
      );
      addRecording(recording);
    }

    if (
      response.type === FETCH_KPI_PRODUCTS.SUCCESS &&
      response.payload &&
      hasCatalogEntitlement
    ) {
      const data = response.payload;
      const dimensionValueIds: string[] = data.rows
        .map((row: any) => row.dimensionValue)
        .filter(
          (dimensionValueId: string) => dimensionValueId !== "Unspecified",
        );

      // Fetch detailed product information for the KPI products
      if (dimensionValueIds.length > 0) {
        const localeCode = yield* select(selectPerformanceReportLocale);
        const catalogId = yield* select(selectPerformanceReportCatalogId);
        let result: any = null;
        if (isProductIdAnalyticIdAPIAvailable) {
          const constName = Object.keys({
            FETCH_PRODUCT_IDS_BY_ANALYTIC_IDS,
          })[0].toString();
          const payload = {
            analyticsIds: dimensionValueIds,
          };
          const headers = {
            "x-locale-code": localeCode || "default",
            "x-currency-code": "USD",
            "x-store-id": storeId,
            "x-catalog-id": catalogId ?? undefined,
          };
          const endpoint = acquireEndpoint(constName);
          result = yield* call(
            callApi,
            FETCH_PRODUCT_IDS_BY_ANALYTIC_IDS,
            postData,
            payload,
            endpoint,
            headers,
          );
        }
        if (
          !isProductIdAnalyticIdAPIAvailable ||
          (result?.type === FETCH_PRODUCT_IDS_BY_ANALYTIC_IDS.SUCCESS &&
            result?.payload)
        ) {
          const productIds = isProductIdAnalyticIdAPIAvailable
            ? (result?.payload?.products?.map(
                (product: any) => product.productId,
              ) ?? [])
            : dimensionValueIds;
          if (
            isProductIdAnalyticIdAPIAvailable &&
            result?.payload?.products?.length > 0
          ) {
            yield* put(
              updateKPIProductsDetails({
                products: result?.payload?.products,
              }),
            );
          }

          if (productIds.length > 0) {
            /**
             * The max number of product details to fetch in a single request
             */
            const PRODUCT_DETAILS_PAGESIZE = +(
              AppEnv.PRODUCT_DETAILS_PAGESIZE ?? 4
            );

            const actionType = FETCH_PRODUCT_LIST;
            const constName = Object.keys({
              FETCH_PRODUCT_LIST,
            })[0].toString();
            const headersObj = {
              "x-locale-code": localeCode || "default",
              "x-currency-code": "USD",
              "x-store-id": storeId,
              "x-catalog-id": catalogId ?? undefined,
            };

            // Fetch product details in chunks
            const allProductColorsList: any[] = [];
            let currentIndex = 0;

            while (currentIndex < productIds.length) {
              const productIdsChunk = productIds.slice(
                currentIndex,
                currentIndex + PRODUCT_DETAILS_PAGESIZE,
              );
              const commaSeperatedProductIds = productIdsChunk.join(",");

              const endpoint = acquireEndpoint(
                constName,
                commaSeperatedProductIds,
                "false",
              );

              const response = yield* call(
                callApi,
                actionType,
                getData,
                { productIds: productIdsChunk },
                endpoint,
                headersObj,
              );

              if (response?.payload?.results) {
                allProductColorsList.push(...response.payload.results);
              }

              currentIndex += PRODUCT_DETAILS_PAGESIZE;
            }

            // Process all collected product colors
            const validProductColors = allProductColorsList?.filter(
              (item: any) => item.cachedProduct && !item.errorId,
            );
            const colorProductIds = validProductColors.map(
              (item: any) => item.productId,
            );

            // Fetch colors in chunks
            if (colorProductIds.length > 0) {
              let colorCurrentIndex = 0;
              while (colorCurrentIndex < colorProductIds.length) {
                const colorProductIdsChunk = colorProductIds.slice(
                  colorCurrentIndex,
                  colorCurrentIndex + PRODUCT_DETAILS_PAGESIZE,
                );

                yield* put({
                  type: FETCH_PRODUCTS_COLORS.REQUEST,
                  payload: {
                    productIds: colorProductIdsChunk,
                    storeId: storeId,
                    localeCode: localeCode || "default",
                    isRefreshingAllProducts: false,
                  },
                });
                // Wait for FETCH_PRODUCTS_COLORS to complete (SUCCESS or FAILURE)
                yield* take([
                  FETCH_PRODUCTS_COLORS.SUCCESS,
                  FETCH_PRODUCTS_COLORS.FAILURE,
                ]);

                colorCurrentIndex += PRODUCT_DETAILS_PAGESIZE;
              }
            }
          }
        }
      }
    }
    yield* put(setKPIProductsLoadingState());
  } catch (e: any) {
    console.error("KPI products fetch error:", e);
    yield* put(setKPIProductsLoadingState());
    yield* put({
      type: FETCH_KPI_PRODUCTS.FAILURE,
      payload: e.message,
    });
  }
}

export function* watchFetchPerformanceMetrics() {
  yield* takeEvery(FETCH_PERFORMANCE_METRICS.REQUEST, fetchPerformanceMetrics);
}

export function* watchFetchPieChartData() {
  yield* takeEvery(FETCH_KPIS_PIE_CHART.REQUEST, fetchPieChartData);
}

export function* watchFetchKPIProducts() {
  yield* takeEvery(FETCH_KPI_PRODUCTS.REQUEST, fetchKPIProducts);
}

function* fetchLocationTreeData(action: FetchLocationTreeDataAction) {
  try {
    const {
      storeId,
      dateRange,
      dimension,
      dimensionBreakdown,
      parentNodeId,
      metricId = "orders",
      limit,
    } = action.payload;

    const serviceType = LOAD_ANALYTICS_SERVICE_URL;
    const constName = Object.keys({
      FETCH_ORDERS_BY_LOCATIONS,
    })[0].toString();
    const endpoint = acquireEndpoint(constName, serviceType);

    const headersObj = {
      "x-store-id": storeId,
    };

    const payload = {
      dateRange,
      dimension: {
        id: dimension,
      },
      metrics: [{ id: metricId }],
      sortBy: {
        metricId,
        desc: true,
      },
      limit,
      parentNodeId,
      dimensionBreakdown: dimensionBreakdown || [],
    };

    if (dimensionBreakdown && dimensionBreakdown.length > 0) {
      payload.dimensionBreakdown = dimensionBreakdown;
    }

    const response = yield* call(
      callApi,
      FETCH_ORDERS_BY_LOCATIONS,
      postData,
      payload,
      endpoint,
      headersObj,
    );

    if (
      isRecordingActive() &&
      response?.type === FETCH_ORDERS_BY_LOCATIONS.SUCCESS
    ) {
      const recording = createRecording(
        {
          dateRange,
          dimension: { id: dimension },
          metrics: [{ id: metricId }],
          sortBy: { metricId, desc: true },
          limit,
          dimensionBreakdown: dimensionBreakdown || [],
        },
        response.payload,
      );
      addRecording(recording);
    }
  } catch (e: any) {
    yield* put({ type: FETCH_ORDERS_BY_LOCATIONS.FAILURE });
  }
}

export function* watchFetchLocationTreeData() {
  yield* takeEvery(FETCH_ORDERS_BY_LOCATIONS.REQUEST, fetchLocationTreeData);
}

function* fetchKPIAnalysis(action: FetchKPIAnalysisAction) {
  const MAX_RETRIES = 10;
  const { storeId, date } = action.payload;

  const serviceType = LOAD_ANALYZER_SERVICE_URL;
  const constName = Object.keys({
    FETCH_KPI_ANALYSIS,
  })[0].toString();
  const endpoint = acquireEndpoint(constName, serviceType, storeId, date);
  const optionsObj = {
    showGenericError: false,
  };

  let retryCount = 0;

  while (retryCount <= MAX_RETRIES) {
    try {
      const response = yield* call(
        callApi,
        FETCH_KPI_ANALYSIS,
        getData,
        null,
        endpoint,
        {},
        optionsObj,
      );

      if (response?.type === FETCH_KPI_ANALYSIS.SUCCESS) {
        return;
      }
      const is503Error = response?.reason === "AnalysisGenerationInProgress";
      const is404Error = response?.reason === "AnalyzerConfigNotFound";

      if (is503Error && retryCount < MAX_RETRIES) {
        const baseRetryAfter = 30;
        // Calculate exponential backoff: baseRetryAfter * (2 ^ retryCount)
        const backoffDelay = baseRetryAfter * Math.pow(2, retryCount);
        const maxBackoff = 300;
        const actualDelay = Math.min(backoffDelay, maxBackoff);
        yield* put({
          type: FETCH_KPI_ANALYSIS.FAILURE,
          payload: {
            retryAfter: actualDelay,
          },
        });
        yield* call(delay, actualDelay * 1000);
        retryCount++;
        continue; // Retry the API call
      } else {
        console.error("KPI Analysis fetch error", response);
        yield* put({
          type: FETCH_KPI_ANALYSIS.FAILURE,
          payload: {
            retryAfter: null,
            is404Error,
          },
        });
        return;
      }
    } catch (e: any) {
      console.error("KPI Analysis unexpected error:", e);
      yield* put({
        type: FETCH_KPI_ANALYSIS.FAILURE,
        payload: {
          error: e?.message || "Failed to fetch KPI analysis",
        },
      });
      return;
    }
  }
  // Exhausted all retries
  console.error(`KPI Analysis failed after ${MAX_RETRIES} retries`);
  yield* put({
    type: FETCH_KPI_ANALYSIS.FAILURE,
    payload: {
      retryAfter: null,
    },
  });
}

export function* watchFetchKPIAnalysis() {
  yield* takeEvery(FETCH_KPI_ANALYSIS.REQUEST, fetchKPIAnalysis);
}

function* fetchDateYearMetrics(action: FetchDateYearMetricsAction) {
  try {
    const actionType = FETCH_DATE_YEAR_METRICS;
    const constName = Object.keys({
      FETCH_DATE_YEAR_METRICS,
    })[0].toString();
    const serviceType = LOAD_ANALYTICS_SERVICE_URL;
    const payload = action.payload;

    const headersObj = {
      "x-store-id": action.payload.storeId,
    };
    const optionsObj = {
      showGenericError: false,
    };

    const endpoint = acquireEndpoint(constName, serviceType);
    const response = yield* call(
      callApi,
      actionType,
      postData,
      payload,
      endpoint,
      headersObj,
      optionsObj,
    );

    if (isRecordingActive() && response?.type === actionType.SUCCESS) {
      const recording = createRecording(
        {
          dateRange: payload.dateRange,
          dimension: payload.dimension,
          metrics: payload.metrics,
          limit: payload.limit,
        },
        response.payload,
      );
      addRecording(recording);
    }
  } catch (e: any) {
    console.error(e);
    yield* put({
      type: FETCH_DATE_YEAR_METRICS.FAILURE,
      message: e.message,
    });
  }
}

export function* watchFetchDateYearMetrics() {
  yield* takeEvery(FETCH_DATE_YEAR_METRICS.REQUEST, fetchDateYearMetrics);
}

function* loadKPIStoreList(action: LoadKPIStoreListAction) {
  const actionType = FETCH_KPI_STORE_LIST;
  const constName = Object.keys({ FETCH_KPI_STORE_LIST })[0].toString();
  const headersObj = {};
  const optionsObj = {
    showGenericError: false,
  };

  try {
    const endpoint = acquireEndpoint(constName);
    yield* call(
      callApi,
      actionType,
      getData,
      null,
      endpoint,
      headersObj,
      optionsObj,
    );
  } catch (e: any) {
    console.error(e);
    yield* put({
      type: FETCH_KPI_STORE_LIST.FAILURE,
      payload: { error: e.message },
    });
  }
}

export function* watchLoadKPIStoreList() {
  yield* takeEvery(LOAD_KPI_STORE_LIST, loadKPIStoreList);
}
